/**
 * MinistrareIA - App Component
 * Main application with routing
 * 
 * UPDATED: 
 * - Fixed routing logic for complete onboarding flow
 * - /connection-setup and /agent-onboarding now accessible during onboarding
 * - Smart redirects based on user onboarding state
 * 
 * ✅ CORREÇÃO APLICADA:
 * - SmartRedirect não usa mais fallback cego para /inbox
 * - Se default_channel não estiver definido, redireciona para /onboarding
 * - Adicionado logs para debug
 * 
 * Flow: Register → ChannelSelection → ConnectionSetup → AgentOnboarding → Inbox
 */
import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { SSEProvider } from './contexts/SSEContext';

// Pages - Auth
import Login from './pages/global/Login';
import Register from './pages/global/Register';
import Onboarding from './pages/global/Onboarding';
import ConnectionSetup from './pages/global/ConnectionSetup';
import AgentOnboarding from './pages/global/AgentOnboarding';

// Pages - Evolution (WhatsApp Local)
import Inbox from './pages/wa-personal/Inbox';

// Pages - WhatsApp Business
import WABusinessInbox from './pages/wa-business/Inbox';

// Pages - Public
import VideoPlayerPage from './pages/public/VideoPlayerPage';
import CheckoutResultPage from './pages/public/CheckoutResultPage';

/**
 * ✅ CORREÇÃO: Smart Redirect Component
 * Redireciona baseado no estado do usuário SEM fallback cego
 */
const SmartRedirect = () => {
  const { user, isAuthenticated, loading } = useAuth();

  if (loading) {
    return <LoadingScreen />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // Check onboarding status
  const onboardingCompleted = user?.onboarding_completed ?? false;
  const defaultChannel = user?.default_channel;

  // ✅ DEBUG: Log para ajudar a identificar problemas
  console.log('[SmartRedirect] Estado do usuário:', {
    onboarding_completed: onboardingCompleted,
    default_channel: defaultChannel,
    user_id: user?.id
  });

  // ✅ CORREÇÃO: Se não tiver canal selecionado, SEMPRE vai para onboarding
  if (!defaultChannel) {
    console.log('[SmartRedirect] Sem canal definido, indo para /onboarding');
    return <Navigate to="/onboarding" replace />;
  }

  // Se onboarding não completado, continuar o fluxo
  if (!onboardingCompleted) {
    console.log('[SmartRedirect] Onboarding não completo, indo para /onboarding');
    return <Navigate to="/onboarding" replace />;
  }

  // ✅ CORREÇÃO: Redirecionar baseado EXATAMENTE no canal salvo
  if (defaultChannel === 'wa_business') {
    console.log('[SmartRedirect] Canal wa_business, indo para /wa-business');
    return <Navigate to="/wa-business" replace />;
  }

  if (defaultChannel === 'wa_personal') {
    console.log('[SmartRedirect] Canal wa_personal, indo para /inbox');
    return <Navigate to="/inbox" replace />;
  }

  // ✅ CORREÇÃO: Se chegou aqui, canal desconhecido - ir para onboarding
  console.warn('[SmartRedirect] Canal desconhecido:', defaultChannel, '- indo para /onboarding');
  return <Navigate to="/onboarding" replace />;
};

/**
 * Protected Route Component
 * Handles authentication and onboarding checks
 */
const ProtectedRoute = ({ children, requiresOnboarding = true }) => {
  const { user, isAuthenticated, loading } = useAuth();

  if (loading) {
    return <LoadingScreen />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // Check if user needs to complete onboarding
  if (requiresOnboarding) {
    const onboardingCompleted = user?.onboarding_completed ?? false;
    const defaultChannel = user?.default_channel;
    
    // ✅ CORREÇÃO: Também verificar se tem canal definido
    if (!onboardingCompleted || !defaultChannel) {
      console.log('[ProtectedRoute] Onboarding incompleto ou sem canal, redirecionando');
      return <Navigate to="/onboarding" replace />;
    }
  }

  return children;
};

/**
 * Authenticated Route Component
 * Only checks authentication, no onboarding checks
 * Used for onboarding flow pages (connection-setup, agent-onboarding)
 */
const AuthenticatedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return <LoadingScreen />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return children;
};

/**
 * Onboarding Route Component
 * Only accessible if user hasn't completed onboarding yet
 */
const OnboardingRoute = ({ children }) => {
  const { user, isAuthenticated, loading } = useAuth();

  if (loading) {
    return <LoadingScreen />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  const onboardingCompleted = user?.onboarding_completed ?? false;
  const defaultChannel = user?.default_channel;
  
  // ✅ CORREÇÃO: Só redirecionar se AMBOS estiverem completos
  if (onboardingCompleted && defaultChannel) {
    console.log('[OnboardingRoute] Onboarding completo, redirecionando para inbox');
    if (defaultChannel === 'wa_business') {
      return <Navigate to="/wa-business" replace />;
    }
    return <Navigate to="/inbox" replace />;
  }

  return children;
};

/**
 * Public Route Component
 * Redirects to appropriate page if already authenticated
 */
const PublicRoute = ({ children }) => {
  const { user, isAuthenticated, loading } = useAuth();

  if (loading) {
    return <LoadingScreen />;
  }

  if (isAuthenticated) {
    const onboardingCompleted = user?.onboarding_completed ?? false;
    const defaultChannel = user?.default_channel;
    
    // ✅ DEBUG
    console.log('[PublicRoute] Usuário autenticado:', {
      onboarding_completed: onboardingCompleted,
      default_channel: defaultChannel
    });
    
    // Se não tem canal, vai para onboarding
    if (!defaultChannel) {
      return <Navigate to="/onboarding" replace />;
    }
    
    // Se onboarding não completado, continuar o fluxo
    if (!onboardingCompleted) {
      return <Navigate to="/onboarding" replace />;
    }

    // ✅ CORREÇÃO: Redirecionar baseado no canal salvo
    if (defaultChannel === 'wa_business') {
      return <Navigate to="/wa-business" replace />;
    }
    
    if (defaultChannel === 'wa_personal') {
      return <Navigate to="/inbox" replace />;
    }
    
    // Canal desconhecido
    return <Navigate to="/onboarding" replace />;
  }

  return children;
};

/**
 * Loading Screen Component
 */
const LoadingScreen = () => (
  <div style={{
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    height: '100vh',
    backgroundColor: '#111b21',
    color: '#e9edef',
    gap: '16px',
  }}>
    <div style={{
      width: '40px',
      height: '40px',
      border: '3px solid #2a3942',
      borderTopColor: '#00a884',
      borderRadius: '50%',
      animation: 'spin 1s linear infinite',
    }}></div>
    <span>Carregando...</span>
    <style>{`
      @keyframes spin {
        from { transform: rotate(0deg); }
        to { transform: rotate(360deg); }
      }
    `}</style>
  </div>
);

/**
 * App Routes
 */
const AppRoutes = () => {
  return (
    <Routes>
      {/* ================================
          PUBLIC ROUTES
          ================================ */}
      <Route
        path="/login"
        element={
          <PublicRoute>
            <Login />
          </PublicRoute>
        }
      />
      <Route
        path="/register"
        element={
          <PublicRoute>
            <Register />
          </PublicRoute>
        }
      />

      {/* ================================
          ONBOARDING FLOW ROUTES
          These routes are part of the onboarding flow
          and should be accessible during the process
          ================================ */}
      
      {/* Step 1: Channel Selection */}
      <Route
        path="/onboarding"
        element={
          <OnboardingRoute>
            <Onboarding />
          </OnboardingRoute>
        }
      />
      
      {/* Step 2: Connection Setup (QR Code or OAuth) */}
      {/* Uses AuthenticatedRoute - only checks if logged in */}
      <Route
        path="/connection-setup"
        element={
          <AuthenticatedRoute>
            <ConnectionSetup />
          </AuthenticatedRoute>
        }
      />
      
      {/* Step 3: Agent Onboarding (Configure AI Agent) */}
      {/* Uses AuthenticatedRoute - only checks if logged in */}
      <Route
        path="/agent-onboarding"
        element={
          <AuthenticatedRoute>
            <AgentOnboarding />
          </AuthenticatedRoute>
        }
      />

      {/* ================================
          PROTECTED ROUTES (Require completed onboarding)
          ================================ */}
      
      {/* WhatsApp Personal Inbox */}
      <Route
        path="/inbox"
        element={
          <ProtectedRoute>
            <Inbox />
          </ProtectedRoute>
        }
      />

      {/* WhatsApp Business Inbox */}
      <Route
        path="/wa-business"
        element={
          <ProtectedRoute>
            <WABusinessInbox />
          </ProtectedRoute>
        }
      />
      <Route
        path="/wa-business/*"
        element={
          <ProtectedRoute>
            <WABusinessInbox />
          </ProtectedRoute>
        }
      />

      {/* ================================
          PUBLIC VIDEO PLAYER (no auth)
          ================================ */}
      <Route path="/v/:videoId" element={<VideoPlayerPage />} />
      <Route path="/v/:videoId/:leadId" element={<VideoPlayerPage />} />

      {/* ================================
          PUBLIC CHECKOUT RESULT (callback do Asaas, no auth)
          /obrigado?p=dr-salus&s=<subscriber_id> | /obrigado (Desafio)
          ================================ */}
      <Route path="/obrigado" element={<CheckoutResultPage kind="obrigado" />} />
      <Route path="/cancelado" element={<CheckoutResultPage kind="cancelado" />} />
      <Route path="/expirado" element={<CheckoutResultPage kind="expirado" />} />

      {/* ================================
          DEFAULT / CATCH-ALL ROUTES
          ================================ */}
      <Route path="/" element={<SmartRedirect />} />
      <Route path="*" element={<SmartRedirect />} />
    </Routes>
  );
};

/**
 * Main App Component
 */
const App = () => {
  return (
    <BrowserRouter>
      <ThemeProvider>
        <AuthProvider>
          <SSEProvider>
            <div className="app">
              <AppRoutes />
            </div>
          </SSEProvider>
        </AuthProvider>
      </ThemeProvider>
    </BrowserRouter>
  );
};

export default App;